import { useMemo } from "react";

import { groupBy } from "lodash";
import moment from "moment";

import { GenericTooltip } from "@components/tooltip-container";
import { useDesignElementManager } from "@hooks/use-design-element-manager";
import { formatting } from "@north-beam/nb-common";
import { useModelMetadata } from "@pages/mmm/hooks/use-model-metadata";
import { Modal } from "@shared/modal";

import { useModelMetadataKpis } from "./hooks/use-model-kpis";
import { formatKpiEquation } from "./utils";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const ModelTrainingDetailsModal = ({ isOpen, onClose }: Props) => {
  const { designElementManager } = useDesignElementManager();
  const [modelMetadata] = useModelMetadata();
  const { kpis } = useModelMetadataKpis();

  const eventsByCategory = useMemo(
    () => groupBy(modelMetadata?.coe, "campaign_type"),
    [modelMetadata?.coe],
  );

  return (
    <Modal
      size="xl"
      isOpen={isOpen}
      onClose={onClose}
      titleContent={
        <div className="font-bold text-xl">Model training details</div>
      }
    >
      <div className="text-sm mt-3">
        <div className="mb-8">
          <h5 className="text-lg font-bold mb-0">Model run date</h5>
          <p className="text-2xl text-cosmic">
            {moment(modelMetadata?.model_run_date).format("MMM DD, YYYY")}
          </p>
        </div>
        <div className="mb-8">
          <h5 className="text-lg font-bold mb-0">General</h5>
          <ul className="list-disc pl-[17px]">
            <li className="mb-3">
              <div className="mb-1">Training date range</div>
              <p className="m-0 text-xs">
                <span className="text-cosmic">
                  {moment(modelMetadata?.training_start_date).format(
                    "MMM DD, YYYY",
                  )}
                </span>
                <span className="text-subtitle px-1">to</span>
                <span className="text-cosmic">
                  {moment(modelMetadata?.training_end_date).format(
                    "MMM DD, YYYY",
                  )}
                </span>
              </p>
            </li>
            <li className="mb-3">
              <div className="mb-1">Out of sample cutoff date</div>
              <p className="m-0 text-xs">
                <span className="text-cosmic">
                  {moment(modelMetadata?.out_of_sample_cutoff_date).format(
                    "MMM DD, YYYY",
                  )}
                </span>
              </p>
            </li>
            <li className="mb-3">
              <div className="mb-1">
                <GenericTooltip
                  content={
                    "HDI Probability refers to a statistical method that identifies the most" +
                    " likely range for a true value, providing a confident basis for informed decisions or predictions."
                  }
                >
                  HDI Probability
                </GenericTooltip>
              </div>
              <p className="m-0 text-xs">
                <span className="text-cosmic">
                  {formatting.formatPercentage(modelMetadata?.hdi_prob, {
                    precision: "exact",
                  })}
                </span>
              </p>
            </li>
          </ul>
        </div>
        {modelMetadata?.target_data_columns && (
          <div className="mb-8">
            <h5 className="text-lg font-bold mb-0">Available KPIs</h5>
            <p className="text-gray+1 mb-2.5">
              Model optimized for all the KPIs bellow. Selected budgets
              prioritize a single KPI.
            </p>
            <ul className="list-disc pl-[17px]">
              {kpis.map((row: any) => (
                <li key={row.kpi_name} className="mb-3">
                  <div className="mb-1">{row.friendly_name}</div>
                  {row.equation && (
                    <p className="m-0 text-xxs text-gray+1">
                      {formatKpiEquation(
                        row.equation,
                        modelMetadata.target_data_columns,
                      ).join(" ")}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        <div>
          <h5 className="text-lg font-bold mb-0">Spend Features</h5>
          <p className="text-gray+1">
            Model trained on all the segments below. Constraints from settings
            in the ‘Explore Budgets’ page are applied after this model run has
            completed.
          </p>
          <ul className=" list-disc space-y-3 pl-[17px]">
            {(modelMetadata?.spend_data_columns ?? []).map((row: any) => {
              const breakdowns: any[] = row.mta_breakdowns || [];

              return (
                <li key={row.column_name}>
                  <div className="flex flex-row space-x-1">
                    {breakdowns.map((e, idx) =>
                      e.key === "Platform (Northbeam)" ? (
                        <div
                          key={`${idx}-${e.value}`}
                          className="text-gray+1 flex items-center gap-2"
                        >
                          {designElementManager
                            .getDesignElement(e.value)
                            .getComponent({ size: "20px" })}
                          {e.value}
                          {idx < breakdowns.length - 1 && breakdowns.length > 1
                            ? " / "
                            : ""}
                        </div>
                      ) : (
                        <div key={`${idx}-${e.value}`}>
                          {e.value}
                          {idx < breakdowns.length - 1 && breakdowns.length > 1
                            ? " / "
                            : ""}
                        </div>
                      ),
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
        {Object.entries(eventsByCategory).map(([category, events]) => {
          return (
            <div key={category}>
              <h5 className="text-lg font-bold mb-4 mt-8 ">{category}s</h5>
              <p className="text-gray+1">
                Used to model when you are running a {category.toLowerCase()}.
                Incorporated into model as a boost to marketing efficiency.
              </p>
              <ul className=" list-disc space-y-3  pl-[17px]">
                {events.map((row) => {
                  return (
                    <li key={JSON.stringify(row)}>
                      <div className="mb-1">{row.description}</div>
                      <p className="m-0 text-xxs text-gray+1">
                        {moment(row.start_date).format("MMM DD, YYYY")} -{" "}
                        {moment(row.end_date).format("MMM DD, YYYY")}
                      </p>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};
